// Talent field visibility conditions.
//
// A field may be gated so that it only shows if one or more conditions are met.
// Conditions are OR-combined: the field shows if ANY condition matches.
//
// Current shape:
//   field.conditions = [{field:<fieldId>, value:<expectedValue>}, ...]
//
// A condition may instead match when the referenced field is empty (unset on
// the talent), which lets a field show by default for talent who haven't set a
// value yet (e.g. show for talent with no `unions` selected):
//   field.conditions = [{field:<fieldId>, empty:true}, ...]
//
// Legacy shape (still read for backwards compatibility, no migration required):
//   field.condition = {field:<fieldId>, value:<expectedValue>}

// Sentinel used by the editor UI to represent the "(empty)" choice in the
// condition value dropdown. Stored conditions use the `empty:true` flag rather
// than this value; it only exists to drive the select control.

export const EMPTY = '__empty__';

// Whether a talent value counts as "set". Unset values (missing key, null,
// empty string, empty multi-select array) are treated as empty.

function isSet(value) {
  if(Array.isArray(value)) {
    return value.length > 0;
  }
  return value !== undefined && value !== null && value !== '';
}

// Whether a resolved talent value satisfies an expected condition value.
// Multi-select fields store their value as an array, so a match means the
// array contains the expected value; single-value fields use strict equality.

function valueMatches(value, expected) {
  if(Array.isArray(value)) {
    return value.indexOf(expected) !== -1;
  }
  return value === expected;
}

// Normalize a field's gating conditions into an array, transparently
// upgrading the legacy singular `condition` shape.

export function getConditions(field) {
  if(!field) {
    return [];
  }
  if(Array.isArray(field.conditions)) {
    return field.conditions.filter(c => !!c && !!c.field);
  }
  if(!!field.condition && !!field.condition.field) {
    return [field.condition];
  }
  return [];
}

// Decide whether a field should be shown given a talent's current values.
// A field with no conditions is always visible. Otherwise it is visible if
// any single condition matches (OR logic). An `empty` condition matches when
// the talent has no value set for the referenced field.

export function isFieldVisible(field, values={}) {
  const conditions = getConditions(field);
  if(conditions.length === 0) {
    return true;
  }
  return conditions.some(c =>
    c.empty ? !isSet(values[c.field]) : valueMatches(values[c.field], c.value)
  );
}
